import React, { useRef, useEffect, useMemo, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useQuery } from 'react-query';
import { aboutService } from '../../../services/aboutService';
import { ChevronLeft, ChevronRight } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const AboutPreview = () => {
  const sectionRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [iconsPerSlide, setIconsPerSlide] = useState(4);

  const { data: aboutContent } = useQuery('aboutContent', aboutService.get);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setIconsPerSlide(1);
      } else if (window.innerWidth < 1024) {
        setIconsPerSlide(2);
      } else {
        setIconsPerSlide(4);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const oemBlock = useMemo(() => {
    const api = aboutContent?.oemClientsSection;

    return (
      api || {
        items: [
          { label: 'HARDWARE DESIGN', iconUrl: '/assets/images/icon1.png' },
          { label: 'SOFTWARE DESIGN', iconUrl: '/assets/images/icon2.png' },
          { label: 'FLEET MONITOR', iconUrl: '/assets/images/icon3.png' },
          { label: 'MANUFACTURING', iconUrl: '/assets/images/icon4.png' },
          { label: 'VEHICLE TRACKER', iconUrl: '/assets/images/icon5.png' },
          { label: 'TEMPERATURE SENSOR', iconUrl: '/assets/images/icon6.png' },
          { label: 'BLE MESH', iconUrl: '/assets/images/icon7.png' },
          { label: '5G NETWORK', iconUrl: '/assets/images/icon8.png' },
          { label: 'COLD CHAINS SOLUTIONS', iconUrl: '/assets/images/cold.png' },
          { label: 'ASSET TRACK', iconUrl: '/assets/images/icon10.png' },
          { label: 'ANALYTICS PRO', iconUrl: '/assets/images/icon11.png' },
          { label: 'GEO FENCE', iconUrl: '/assets/images/icon12.png' },
        ]
      }
    );
  }, [aboutContent]);

  const totalSlides = Math.ceil(oemBlock.items.length / iconsPerSlide);

  useEffect(() => {
    setCurrentIndex(0);
  }, [iconsPerSlide]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % totalSlides);
    }, 3500);

    return () => clearInterval(interval);
  }, [totalSlides]);

  const nextSlide = () => setCurrentIndex((prev) => (prev + 1) % totalSlides);
  const prevSlide = () => setCurrentIndex((prev) => (prev - 1 + totalSlides) % totalSlides);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        '.oem-head',
        { opacity: 0, y: 24 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 75%'
          }
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="pt-8 pb-20 md:pt-10 md:pb-24 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="oem-head text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight uppercase text-gray-900/80">
            Why Choose SiWi?
          </h2>

          <div className="mt-6 max-w-5xl mx-auto space-y-6">
            <p className="text-base md:text-lg lg:text-xl text-gray-500 leading-relaxed">
              At SiWi, we don't just manufacture{' '}
              <span className="bg-yellow-100 text-gray-700 px-2 py-1 rounded-sm">
                GPS tracking devices
              </span>
              —we engineer comprehensive location intelligence solutions that transform how businesses monitor, manage,
              and optimize their mobile assets across the globe.
            </p>

            <p className="text-base md:text-lg lg:text-xl text-gray-500 leading-relaxed">
              Our commitment to{' '}
              <span className="bg-yellow-100 text-gray-700 px-2 py-1 rounded-sm">
                innovation, quality, and customer success
              </span>{' '}
              distinguishes us in a crowded marketplace.
            </p>
          </div>
        </div>

        <div className="relative mt-16 px-8 sm:px-12 lg:px-16">
          <button
            onClick={prevSlide}
            className="absolute left-0 sm:left-2 top-1/2 -translate-y-1/2 z-20 text-gray-500 hover:text-black transition p-2"
            aria-label="Previous icons"
          >
            <ChevronLeft size={32} className="sm:w-10 sm:h-10" />
          </button>

          <button
            onClick={nextSlide}
            className="absolute right-0 sm:right-2 top-1/2 -translate-y-1/2 z-20 text-gray-500 hover:text-black transition p-2"
            aria-label="Next icons"
          >
            <ChevronRight size={32} className="sm:w-10 sm:h-10" />
          </button>

          <div className="overflow-hidden">
            <div
              className="flex transition-transform duration-700 ease-in-out"
              style={{
                width: `${totalSlides * 100}%`,
                transform: `translateX(-${(100 / totalSlides) * currentIndex}%)`
              }}
            >
              {Array.from({ length: totalSlides }).map((_, slideIndex) => (
                <div
                  key={slideIndex}
                  className="flex justify-center items-center gap-8 sm:gap-12 lg:gap-24"
                  style={{ width: `${100 / totalSlides}%` }}
                >
                  {oemBlock.items
                    .slice(slideIndex * iconsPerSlide, slideIndex * iconsPerSlide + iconsPerSlide)
                    .map((item, idx) => (
                      <div key={idx} className="w-full sm:w-56 lg:w-64 text-center group px-2">
                        <div className="mx-auto w-32 h-32 sm:w-36 sm:h-36 lg:w-44 lg:h-44 flex items-center justify-center">
                          <img
                            src={item.iconUrl}
                            alt={item.label}
                            className="w-28 h-28 sm:w-32 sm:h-32 lg:w-40 lg:h-40 object-contain transition-transform duration-300 group-hover:scale-110"
                            loading="lazy"
                          />
                        </div>

                        <div className="mt-4 text-sm sm:text-base lg:text-lg font-semibold tracking-wide text-gray-900 uppercase">
                          {item.label}
                        </div>
                      </div>
                    ))}
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default AboutPreview;